import { ref, getBytes } from 'firebase/storage'
import { ARTIFACT_MANIFEST_PATH, type ArtifactManifest } from '@shared/artifacts'
import {
  SEARCH_ARTIFACT_KEY, SEARCH_INDEX_SCHEMA, decodeSearchIndex,
  type SearchIndex, type SearchIndexFile,
} from '@shared/searchIndex'
import { storage, isOtaBoot } from './firebase'
import { log } from './logger'

// Module-level store for the global search index (see shared/searchIndex.ts).
// The raw file persists in IndexedDB so search works offline on site; the
// manifest is re-checked at most every MANIFEST_TTL_MS for newer versions.

export interface SearchIndexSnapshot {
  status: 'idle' | 'loading' | 'ready' | 'error'
  index?: SearchIndex
}

interface StoredIndex {
  schema: number
  version: string
  file: SearchIndexFile
}

const MANIFEST_TTL_MS = 10 * 60_000
const IDB_TIMEOUT_MS = 2000
const DB_NAME = 'beacon-artifacts'
const STORE = 'kv'
const KEY = 'search'

let snapshot: SearchIndexSnapshot = { status: 'idle' }
let inflight: Promise<void> | null = null
let lastManifestCheck = 0
let triedDisk = false
const listeners = new Set<() => void>()

function setSnapshot(next: SearchIndexSnapshot) {
  snapshot = next
  listeners.forEach(l => l())
}

export function getSearchIndexSnapshot(): SearchIndexSnapshot {
  return snapshot
}

export function subscribeSearchIndex(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

/** Loads the cached index, then refreshes it from Storage when due. Single-flight; safe to call often. */
export function ensureSearchIndex(): Promise<void> {
  inflight ??= load().finally(() => { inflight = null })
  return inflight
}

async function load() {
  if (!snapshot.index) setSnapshot({ ...snapshot, status: 'loading' })
  try {
    if (!snapshot.index && !triedDisk && !isOtaBoot) {
      triedDisk = true
      const stored = await withTimeout(idbGet())
      if (stored?.schema === SEARCH_INDEX_SCHEMA) {
        setSnapshot({ status: 'ready', index: decodeSearchIndex(stored.file, stored.version) })
      }
    }

    const due = !snapshot.index || Date.now() - lastManifestCheck > MANIFEST_TTL_MS
    if (!navigator.onLine || !due) {
      if (!snapshot.index) setSnapshot({ status: 'idle' })
      return
    }
    lastManifestCheck = Date.now()
    const manifest = JSON.parse(await fetchText(ARTIFACT_MANIFEST_PATH, 64 * 1024)) as ArtifactManifest
    const entry = manifest.artifacts[SEARCH_ARTIFACT_KEY]
    if (!entry) throw new Error(`manifest has no ${SEARCH_ARTIFACT_KEY}`)
    if (entry.version === snapshot.index?.version) {
      setSnapshot({ ...snapshot, status: 'ready' })
      return
    }

    const file = JSON.parse(await fetchText(entry.path, 5 * 1024 * 1024, true)) as SearchIndexFile
    setSnapshot({ status: 'ready', index: decodeSearchIndex(file, entry.version) })
    log('search', 'index loaded', { version: entry.version, events: file.events.length })
    if (!isOtaBoot) {
      void withTimeout(idbPut({ schema: SEARCH_INDEX_SCHEMA, version: entry.version, file }))
    }
  } catch (err) {
    log('search', 'index load failed', { error: String(err) })
    // Keep a stale index over nothing; a failed check retries on the next open.
    lastManifestCheck = 0
    setSnapshot(snapshot.index ? { ...snapshot, status: 'ready' } : { status: 'error' })
  }
}

async function fetchText(path: string, maxBytes: number, gzipped = false): Promise<string> {
  const bytes = await getBytes(ref(storage, path), maxBytes)
  if (!gzipped) return new TextDecoder().decode(bytes)
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))
  return new Response(stream).text()
}

// IndexedDB can hang in WKWebView; never let it block search.
function withTimeout<T>(p: Promise<T>): Promise<T | undefined> {
  return Promise.race([
    p.catch(() => undefined),
    new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), IDB_TIMEOUT_MS)),
  ])
}

/** Drops the on-disk index so the next search downloads it again. */
export function clearSearchIndexCache(): Promise<void> {
  return withTimeout(new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })).then(() => undefined)
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function idbGet(): Promise<StoredIndex | undefined> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(KEY)
    req.onsuccess = () => resolve(req.result as StoredIndex | undefined)
    req.onerror = () => reject(req.error)
  })
}

async function idbPut(value: StoredIndex): Promise<void> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(value, KEY)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}
